import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { Button } from "@/uiComponents/components/ui/button";
import {
  PaymentMethodsLabels,
  PaymentMethodsValues,
} from "@/enums/expenseFields";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "@/utils/localStorageHelper";
import { localStorageKey } from "@/enums/localStorageKey";
import { GetExpense } from "@/types/expense";
import { toastMessage } from "@/utils/toastHelper";
import { TiTick } from "react-icons/ti";

const ExportPdf = () => {
  const formattedExpenses = getLocalStorageItem(
    localStorageKey.EXPENSES
  ) as GetExpense[];

  const allExpenses = useSelector(
    (state: RootState) => state.graph.expensesForGraph
  );
  const expenses = useSelector(
    (state: RootState) => state.expense.getExpensesResponse.results
  );
  const filters = useSelector((state: RootState) => state.filter.filter);

  let filteredExpenses;
  if (filters !== null) {
    const now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(now.getMonth() - 1);

    filteredExpenses = allExpenses.filter((expense) => {
      const expenseDate = new Date(expense.date);
      return expenseDate >= oneMonthAgo && expenseDate <= now;
    });
  } else {
    filteredExpenses = expenses;
  }

  if (filteredExpenses && filteredExpenses.length > 0) {
    setLocalStorageItem(localStorageKey.EXPENSES, filteredExpenses);
  }
  const totalAmount = formattedExpenses?.reduce(
    (sum, expense) => sum + Number(expense.amount),
    0
  );

  const handleSavePdfDirectly = () => {
    const doc = new jsPDF({ orientation: "landscape" });

    const tableData = formattedExpenses.map((expense) => [
      expense.date,
      expense.description,
      expense.amount,
      expense.categoryName,
      expense.branchName,
      expense.paymentMethod,
      expense.items
        .slice(0, 2)
        .map((tag) => tag.name)
        .join(", "),
      expense.userName,
      expense.reimbursed ? "Yes" : "No",
    ]);

    autoTable(doc, {
      head: [
        [
          "Date",
          "Description",
          "Amount",
          "Category",
          "Branch",
          "Payment Method",
          "Tags",
          "Created By",
          "Reimbursed",
        ],
      ],
      body: tableData,
      styles: { fontSize: 10 },
    });

    autoTable(doc, {
      body: [
        [
          {
            content: "Total Amount",
            colSpan: 3,
            styles: { halign: "left", fontStyle: "bold" },
          },
          {
            content: `Rs. ${totalAmount}`,
            colSpan: 1,
            styles: { halign: "left", fontStyle: "bold" },
          },
        ],
      ],
      startY: (doc as any).lastAutoTable.finalY + 5,
      theme: "plain",
    });

    doc.save("Expense_Report.pdf");
  };

  const handlePrint = () => {
    try {
      const printContent =
        document.querySelector(".printable-content")?.innerHTML;

      if (!printContent) {
        toastMessage("error", "No printable content found");
        return;
      }

      const printWindow = window.open("Expense", "", "width=800,height=600");

      if (!printWindow) {
        toastMessage(
          "error",
          "Popup was blocked. Please allow popups for this site."
        );
        return;
      }

      printWindow.document.write(`
  <!DOCTYPE html>
  <html>
    <head>
      <title></title> <!-- Empty title -->
      <style>
        @media print {
          @page {
            margin: 10mm;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f2f2f2;
          }
        }
      </style>
    </head>
    <body>
    <h1>Expense Report </h1>
      ${printContent}
      <script>
        window.onload = () => {
          setTimeout(() => {
            window.print();
            window.close();
          }, 300);
        };
      </script>
    </body>
  </html>
`);
      printWindow?.document.close();
    } catch (error) {
      console.error("Print failed:", error);
      alert("Printing failed. Please try again.");
    }
  };

  return (
    <div className="p-6 print:p-0 overflow-x-auto">
      <div className="flex justify-between items-center mb-4 print:hidden">
        <h1 className="text-2xl text-center font-semibold">
          Printable Expenses Report
        </h1>
        <div>
        <Button
          onClick={handlePrint}
          className="bg-[#191F29] mr-2 hover:bg-[#2A313D] active:bg-[#001533] text-white hover:text-white"
        >
          Print / Save as PDF
        </Button>
        <Button
          onClick={handleSavePdfDirectly}
          className="bg-[#191F29] hover:bg-[#2A313D] active:bg-[#001533] text-white hover:text-white"
        >
          {"Save as PDF"}
        </Button>
        </div>
        
      </div>

      {formattedExpenses?.length === 0 ? (
        <p>No expenses to export.</p>
      ) : (
        <div className="printable-content">
          <table className="w-full border-collapse border border-gray-300 text-sm print:text-xs ">
            <thead>
              <tr>
                <th className="border p-2">Date</th>
                <th className="border p-2">Description</th>
                <th className="border p-2">Amount</th>
                <th className="border p-2">Category</th>
                <th className="border p-2">Branch</th>
                <th className="border p-2">Payment Method</th>
                <th className="border p-2">Tags</th>
                <th className="border p-2">Created By</th>
                <th className="border p-2">Reimbursed</th>
              </tr>
            </thead>
            <tbody>
              {formattedExpenses?.map((expense) => (
                <tr key={expense.id}>
                  <td className="border p-2 text-center">{expense.date}</td>
                  <td className="border p-2">{expense.description}</td>
                  <td className="border p-2 text-center">{expense.amount}</td>
                  <td className="border p-2 text-center">
                    {expense.categoryName}
                  </td>
                  <td className="border p-2 text-center">
                    {expense.branchName}
                  </td>
                  <td className="border p-2 text-center">
                    {" "}
                    {expense.paymentMethod === PaymentMethodsValues.QR
                      ? PaymentMethodsLabels.ONLINE
                      : expense.paymentMethod === PaymentMethodsValues.CHEQUE
                        ? PaymentMethodsLabels.CHEQUE
                        : expense.paymentMethod[0].toUpperCase() +
                          expense.paymentMethod.slice(1)}
                  </td>
                  <td className="border p-2 text-center">
                    {expense.items
                      .slice(0, 2)
                      // @ts-expect-error: skip next line
                      .map((tag) => tag.name)
                      .join(", ")}
                    {expense.items.length > 2 && " ..."}
                  </td>
                  <td className="border p-2 text-center">{expense.userName}</td>
                  <td className="border p-2 text-center">
                    {expense.reimbursed ? (
                      <TiTick className="text-green-500 mx-auto" size={20} />
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
              ))}
              <tr className="border-0">
                <th className="text-center font-bold p-2">Total Amount </th>
                <th></th>
                <th className="text-center font-bold p-2">Rs. {totalAmount}</th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExportPdf;
